import { Component } from '@theme/component';

// Supplier headings as Printify renders them, and the row each one belongs to.
// The introduction before the first heading stays in the description row.
const rowForHeading = new Map([
  ['product features', 'features'],
  ['product details', 'description'],
  ['care instructions', 'care'],
  ['eu representative', 'compliance'],
  ['product information', 'compliance'],
  ['warnings, hazard', 'compliance'],
]);

// Printify's GPSR "Warnings, Hazard" field also carries the blank's country of
// origin ("Made in Honduras", "Blank product sourced from Japan (Printify
// Choice)"). Origin is not a hazard (IAMG-61), so those statements are dropped
// and any real warning in the same field ("For adults") stays.
const HAZARD_LABEL = 'warnings, hazard';
const ORIGIN_STATEMENT = /^(made in|blank product sourced from)\s/i;

const MEDIA = 'img, picture, video, iframe, svg';
const BLOCK_TAGS = new Set(['P', 'DIV', 'LI', 'UL', 'OL', 'BR', 'HR', 'TABLE', 'TR', 'TD', 'TH', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'BLOCKQUOTE', 'PRE', 'DL', 'DT', 'DD', 'SECTION', 'ARTICLE']);

/**
 * Plain text with a space at every block boundary, so list items never run together.
 * @param {Node} node
 * @returns {string}
 */
function textOf(node) {
  if (node instanceof Text) return node.data;
  if (!(node instanceof Element)) return '';
  const inner = Array.from(node.childNodes, textOf).join('');
  return BLOCK_TAGS.has(node.tagName) ? ` ${inner} ` : inner;
}

/** @param {string} text */
const normalize = (text) => text.replace(/\s+/g, ' ').trim().toLowerCase();
/** @param {Node} node */
const hasMedia = (node) => node instanceof Element && (node.matches(MEDIA) || node.querySelector(MEDIA) !== null);
/** @param {Node} node */
const isBlank = (node) => !normalize(textOf(node)) && !hasMedia(node);

/**
 * The leading <strong> label of a top-level supplier paragraph. Nested tables,
 * quotes, links and translated or unknown headings do not match.
 * @param {Node} node
 * @returns {string | null}
 */
function labelOf(node) {
  if (!(node instanceof HTMLParagraphElement)) return null;
  const first = [...node.childNodes].find((child) => child.textContent?.trim());
  return first instanceof HTMLElement && first.tagName === 'STRONG' ? first.textContent.trim() : null;
}

/**
 * @typedef {{ key: string, label: string, nodes: Node[] }} Block
 * A run of top-level nodes that starts at a recognized heading paragraph and
 * ends before the next one.
 */

/**
 * @param {Element} source
 * @returns {Block[]}
 */
function splitBlocks(source) {
  /** @type {Block[]} */
  const blocks = [{ key: 'description', label: '', nodes: [] }];
  for (const node of [...source.childNodes]) {
    const label = labelOf(node);
    if (label !== null) blocks.push({ key: rowForHeading.get(label.toLowerCase()) ?? 'description', label, nodes: [] });
    blocks[blocks.length - 1].nodes.push(node);
  }
  return blocks.filter((block) => block.nodes.length);
}

/**
 * Block text without its label, for recognizing a supplier block that repeats
 * one already on the page ("Care instructions: Wipe clean." after the care list).
 * @param {Node[]} nodes
 * @param {string} label
 */
function bodyOf(nodes, label) {
  const text = normalize(nodes.map(textOf).join(' '));
  const prefix = normalize(label);
  const body = prefix && text.startsWith(prefix) ? text.slice(prefix.length) : text;
  return body.replace(/^[\s:]+/, '').replace(/[\s.]+$/, '');
}

/**
 * Remove whitespace, line breaks and empty paragraphs from both ends of a block.
 * @param {Node[]} nodes
 * @returns {Node[]}
 */
function trimBlank(nodes) {
  let start = 0;
  let end = nodes.length;
  while (start < end && isBlank(nodes[start])) start += 1;
  while (end > start && isBlank(nodes[end - 1])) end -= 1;
  for (const node of [...nodes.slice(0, start), ...nodes.slice(end)]) node.parentNode?.removeChild(node);
  return nodes.slice(start, end);
}

/** @param {Node[]} nodes */
const discard = (nodes) => nodes.forEach((node) => node.parentNode?.removeChild(node));

/**
 * Remove country-of-origin statements from a supplier hazard paragraph,
 * keeping its label element and every other comma-separated statement as text.
 * A paragraph left without statements is removed.
 * @param {Node | undefined} paragraph - The block's heading paragraph.
 */
function dropOriginStatements(paragraph) {
  if (!paragraph || labelOf(paragraph) === null) return;
  const label = /** @type {HTMLElement} */ ([...paragraph.childNodes].find((child) => child.textContent?.trim()));
  const rest = [];
  for (let node = label.nextSibling; node; node = node.nextSibling) rest.push(node);
  const statements = rest.map(textOf).join('').replace(/^\s*:\s*/, '').split(',').map((part) => part.trim()).filter(Boolean);
  const kept = statements.filter((statement) => !ORIGIN_STATEMENT.test(statement));
  if (kept.length === statements.length) return;
  if (!kept.length) {
    paragraph.parentNode?.removeChild(paragraph);
    return;
  }
  discard(rest);
  label.after(`: ${kept.join(', ')}`);
}

/**
 * @typedef {{ row: HTMLDetailsElement, content: Element, seed: string, seen: Set<string> }} Row
 * `seed` is the row's server-rendered text (authored care, shared policy);
 * `seen` holds "label|body" keys of the supplier blocks already appended.
 */

/**
 * Organize existing supplier HTML without rewriting product data. The server
 * renders the complete description in an open native disclosure first, so
 * missing JavaScript or unfamiliar markup never makes the copy disappear.
 *
 * With JavaScript, the supplier's feature list opens first, the introduction
 * collapses behind it, and care and compliance blocks get their own closed
 * rows. Row headings come from the template's data-heading-* attributes.
 * @extends {Component<{legacyDescription?: HTMLDivElement}>}
 */
class ProductEditorialLegacy extends Component {
  // Some morph paths update the existing children without notifying their
  // connected parent component. Observe the fresh source ref in that case.
  #sourceObserver = new MutationObserver(() => this.updatedCallback());

  connectedCallback() {
    super.connectedCallback();
    this.#sourceObserver.observe(this, { childList: true, subtree: true, attributes: true, attributeFilter: ['ref'] });
    this.#organizeDescription();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#sourceObserver.disconnect();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#organizeDescription();
  }

  #organizeDescription() {
    const source = this.refs.legacyDescription;
    const originalRow = source?.closest('details');
    if (!source || !(originalRow instanceof HTMLDetailsElement)) return;
    // A consumed ref makes reconnects and repeated updatedCallback calls
    // idempotent. A Section Rendering API morph supplies a fresh source ref.
    source.removeAttribute('ref');

    /** @type {Map<string, Row>} */
    const rows = new Map([['description', { row: originalRow, content: source, seed: '', seen: new Set() }]]);
    for (const block of splitBlocks(source)) {
      const heading = block.key === 'description' ? originalRow.querySelector('summary h2')?.textContent : this.#headingFor(block.key);
      if (heading == null) continue;

      // A supplier label that only repeats the row title is not shown twice.
      const [first] = block.nodes;
      if (first && normalize(block.label) === normalize(heading) && normalize(textOf(first)) === normalize(heading)) {
        first.parentNode?.removeChild(first);
        block.nodes.shift();
      }
      if (normalize(block.label) === HAZARD_LABEL) {
        dropOriginStatements(block.nodes[0]);
        block.nodes = block.nodes.filter((node) => node.parentNode);
      }
      const nodes = trimBlank(block.nodes);
      const body = bodyOf(nodes, block.label);
      const media = nodes.some(hasMedia);
      if (!body && !media) {
        discard(nodes);
        continue;
      }

      const target = rows.get(block.key) ?? this.#rowFor(block.key, heading, originalRow);
      rows.set(block.key, target);
      // A block repeats when the same label carries the same text again, or
      // when its text is the row's authored content. Different labels with the
      // same value ("Product information: None", "Warnings, Hazard: None") are
      // distinct statements and both stay.
      const key = `${normalize(block.label)}|${body}`;
      if (body && !media && (target.seen.has(key) || body === target.seed)) {
        discard(nodes);
        continue;
      }
      target.seen.add(key);
      if (target.content !== source) target.content.append(...nodes);
    }

    // Assign the state rather than toggle it: a morph carries the previous
    // open state across, and the features row must be the one that is open.
    originalRow.open = !rows.has('features');
    if (isBlank(source)) originalRow.remove();
  }

  /**
   * The template-provided heading for a row the component may create.
   * @param {string} key
   * @returns {string | undefined}
   */
  #headingFor(key) {
    return this.dataset[`heading${key[0].toUpperCase()}${key.slice(1)}`] || undefined;
  }

  /**
   * Find the server-rendered row for a key, or create one. An authored care
   * row or shared policy row is kept; supplier blocks are appended to it so
   * neither source replaces the other silently.
   * @param {string} key
   * @param {string} heading
   * @param {HTMLDetailsElement} originalRow
   * @returns {Row}
   */
  #rowFor(key, heading, originalRow) {
    let row = this.querySelector(`details[data-editorial-row="${key}"]`);
    let content = row?.querySelector('.product-editorial__row-content');
    if (!(row instanceof HTMLDetailsElement) || !content) {
      row = /** @type {HTMLDetailsElement} */ (originalRow.cloneNode(false));
      row.id = originalRow.id.replace(/-description$/, `-${key}`);
      row.dataset.editorialRow = key;
      row.open = key === 'features';
      const summary = /** @type {HTMLElement} */ (originalRow.querySelector('summary')?.cloneNode(true) ?? document.createElement('summary'));
      const title = summary.querySelector('h2') ?? summary.appendChild(document.createElement('h2'));
      title.textContent = heading;
      content = document.createElement('div');
      content.className = originalRow.querySelector('.product-editorial__row-content')?.className ?? 'product-editorial__row-content rte';
      row.append(summary, content);
      this.insertBefore(row, this.#anchorFor(key, originalRow));
    }
    return { row, content, seed: bodyOf([content], ''), seen: new Set() };
  }

  /**
   * Created rows keep the page order: features before the description, care
   * before compliance, compliance last.
   * @param {string} key
   * @param {HTMLDetailsElement} originalRow
   * @returns {Node | null}
   */
  #anchorFor(key, originalRow) {
    if (key === 'features') return originalRow;
    if (key === 'care') return this.querySelector('details[data-editorial-row="compliance"]');
    return null;
  }
}

if (!customElements.get('product-editorial-legacy')) {
  customElements.define('product-editorial-legacy', ProductEditorialLegacy);
}
